import { useState } from 'react';
import { Container, Grid, Hidden, makeStyles } from '@material-ui/core';
import { ExperienceSearchProvider } from 'contexts/ExperienceSearchContext';

import Profile from '../components/Home/Profile';
import ProfileV2 from '../components/Home/ProfileV2';

import Statuses from '../components/Home/Statuses';
import { useGetCurrentUserQuery, useGetForecastsQuery, useGetGroupsQuery, useUserSearchQuery } from 'utils/wemble-api';
import classNames from 'classnames';
import { UserSearchParams } from 'utils/wemble-api.generated';
import SearchFilter from 'components/Home/SearchFilter/SearchFilter';
import organization from 'utils/organization';
import { renderCustomHtml } from 'utils/customHtml';
import Forecasting from '../components/Home/Forecast/Forecasting';
import VacationPlanner from 'components/vacationPlanner/VacationPlanner';
import MatterComments from 'components/Home/MatterComments';
import moment from 'moment';
import { getToday } from 'components/Home/Forecast/utils';
import DaySelect from 'components/Home/DaySelect';
import HalfGrid from 'components/Home/HalfGrid';
import SingleBoxButton from 'components/Home/SingleBoxButton';

import home from './../assets/icons/home.svg';
import calendar from './../assets/icons/calendar-padded.svg';
import UserMessageInput from 'components/Home/UserMessageInput';
import Modal from 'components/shared/Modal';
import WorkplaceOverview from 'components/workplaceOverview/WorkplaceOverview';
import { useSetMobilePageTitle } from 'contexts/MobilePageTitleContext';

const useStyles = makeStyles((theme) => ({
  container: {
    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },
  halfGridItem: {
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      marginTop: 8
    },

  },
  root: {
    width: '100%',
    margin: '70px auto 20px',
    [theme.breakpoints.down('md')]: {
      marginTop: '-20px'
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: '25px',
    },
    [theme.breakpoints.down('xs')]: {
      paddingTop: '75px',
    },

  },
  welcome: {
    fontSize: 22,
    fontWeight: 600,
    position: 'relative',
    left: 20,
    [theme.breakpoints.down('md')]: {
      fontSize: 18,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  grid: {
    margin: '14px 0 20px',
    backgroundColor: 'white',
    width: '100%',
    padding: 16,
    borderRadius: '26px 0 26px 26px',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
      borderRadius: '24px 0 24px 24px',
    },
  },
  gridHidden: {
    minHeight: 'unset',
    padding: 0,
  },
  messageInput: {
    paddingRight: 10,
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      paddingBottom: 6,
    }
  },
  // .root has paddingTop: 75 on xs to make room for the Profile tab when there is no
  // customHtml (the tab pokes up out of .grid via top: -68). When customHtml IS rendered,
  // its own height pushes .grid down, so we don't need that headroom — pull the customHtml
  // up into the padding via negative margin.
  // paddingBottom keeps clear of the Profile tab's full-width poke-up on mobile portrait.
  customHtml: {
    [theme.breakpoints.down('xs')]: {
      marginTop: -55,
      paddingBottom: 80,
    },
  },
}));

const Home = () => {
  const classes = useStyles();
  useSetMobilePageTitle('Wemble');
  const [workplaceOverview, setWorkplaceOverviewOpen] = useState(false);
  const [vacationPlannerOpen, setVacationPlannerOpen] = useState(false);
  const { data: user } = useGetCurrentUserQuery(undefined, { refetchOnMountOrArgChange: 5, refetchOnReconnect: true });

  // New search endpoint — skip until SearchFilter has initialized with actual filters
  const [searchParams, setSearchParams] = useState<UserSearchParams | null>(null);
  const { data: searchResult, isFetching: isSearchFetching } = useUserSearchQuery(searchParams as UserSearchParams, { skip: !searchParams, refetchOnMountOrArgChange: 3, refetchOnReconnect: true, refetchOnFocus: true });

  const { data: groupsRes } = useGetGroupsQuery();

  const matterLayout = organization(user?.company).enableUtilizationView && !organization(user?.company).detailedViewAdminDefault && user?.detailedViewPriviliges && !organization(user?.company).disableMatterLayout

  const { data: allForecasts } = useGetForecastsQuery({}, { refetchOnReconnect: true });
  const today = getToday()
  const forecasts = allForecasts?.filter(forecast => forecast.user === user?._id && moment(forecast.endDate).isAfter(today)).sort((a, b) => moment(a.startDate).diff(moment(b.startDate))) || [];
  const currentForecast = forecasts.find(forecast => moment(forecast.startDate) <= moment(getToday()))

  const users = searchResult?.users
  const showWFH = !organization(user?.company).disableWFH

  return (
    <>
      <Container className={classes.container}>
        <div className={classes.root}>
          {(groupsRes?.customHtml && user && organization(user?.company).enableCustomHtml) ? <div
            className={classes.customHtml}
            dangerouslySetInnerHTML={{ __html: renderCustomHtml(groupsRes.customHtml, user) }}
          />
            : <h1 className={classes.welcome}>Welcome back, {user?.name}</h1>}

          <div className={classNames(classes.grid, { [classes.gridHidden]: user?.admin })}>
            {matterLayout ? (
              <Profile
                user={user}
                openVacationPlanner={(() => setVacationPlannerOpen(true))}
                openWorkplaceOverview={showWFH ? (() => setWorkplaceOverviewOpen(true)) : undefined}
                fetchUsersWFH={((matterLayout || organization(user?.company).expandComment) && showWFH && !Boolean(user?.admin)) ? (async () => {}) : undefined}
              />
            ) : (
              showWFH ? (
                <Profile
                  user={user}
                  openVacationPlanner={Boolean(user?.admin) ? (() => setVacationPlannerOpen(true)) : false}
                  openWorkplaceOverview={Boolean(user?.admin) ? (() => setWorkplaceOverviewOpen(true)) : undefined}
                  fetchUsersWFH={((matterLayout || organization(user?.company).expandComment) && showWFH && !Boolean(user?.admin)) ? (async () => {}) : undefined}
                />
              ) : (
                <ProfileV2
                  user={user}
                  openVacationPlanner={(() => setVacationPlannerOpen(true))}
                />
              )
            )}

            {!Boolean(user?.admin) && matterLayout && (<Grid spacing={3} container>
              <Grid item xs={12} md={7} >
                <Forecasting />
              </Grid>

              <Grid item xs={12} md={5}>
                <MatterComments />
                <Hidden smUp>
                  {showWFH && (<div style={{ height: 100, marginTop: 14 }}>
                    <DaySelect user={user} disabled={Boolean(user?.admin)} fetchUsers={async () => {}} />
                  </div>)}
                </Hidden>

              </Grid>


            </Grid>)}

            {(!Boolean(user?.admin) && !matterLayout) && (showWFH ? (
              <Grid spacing={2} container>
                <Grid item xs={12} md={8} >
                  <Forecasting />
                  <Hidden smUp>
                    {showWFH && (
                      <div style={{ height: 100, marginTop: 14 }}>
                        <DaySelect user={user} disabled={Boolean(user?.admin)} fetchUsers={async () => {}} />
                      </div>
                    )}
                  </Hidden>
                </Grid>

                <Grid item xs={12} md={4}>
                  <HalfGrid>
                    <Grid container>
                      <Grid item xs={12} md={organization(user?.company).expandComment ? 12 : 6} style={{ paddingRight: organization(user?.company).expandComment ? 0 : 10 }} className={classes.messageInput}>
                        <UserMessageInput disabled={Boolean(user?.admin)} />
                      </Grid>


                      <Grid container spacing={1} style={{ marginTop: 4 }}>
                        <Grid
                          item
                          xs={6}
                          md={6}
                        >
                          <SingleBoxButton
                            icon={home}
                            text={'WFH Overview'}
                            event={() => setWorkplaceOverviewOpen(true)}
                          />
                        </Grid>
                        {!Boolean(organization(user?.company)?.disableAbsencePlanner) && (
                          <Grid
                            item
                            xs={6}
                            md={6}
                          >
                            <SingleBoxButton
                              icon={calendar}
                              text={'Calendar view'}
                              event={() => setVacationPlannerOpen(true)}
                            />
                          </Grid>
                        )}
                      </Grid>

                    </Grid>

                  </HalfGrid>




                </Grid>
              </Grid>



            ) : (<Forecasting />))}
          </div>
        </div>

        <ExperienceSearchProvider value={{ experienceAnnotations: searchResult?.experienceAnnotations, experienceScope: searchParams?.experienceScope }}>
          <Statuses
            users={users}
            isFetching={isSearchFetching}
            officeOrGroupSelected={true}
            searchParams={searchParams || undefined}
            filterBar={
              <SearchFilter
                facets={searchResult?.facets}
                onChange={setSearchParams}
              />
            }
          />
        </ExperienceSearchProvider>


      </Container>

      <VacationPlanner
        isOpen={vacationPlannerOpen}
        close={() => setVacationPlannerOpen(false)}
        selectedUser={user?._id}
        users={users}
      />

      <Modal visible={workplaceOverview} close={() => setWorkplaceOverviewOpen(false)} customWidth={900}>
        <WorkplaceOverview users={users} currentUser={user} />
      </Modal>


    </>
  );
};

export default Home;
